import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import CidSearch from '../components/CidSearch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ConvenioSelect } from '../components/financeiro/ConvenioSelect';
import { ArrowLeft, Save, Loader2, AlertCircle, User } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_LABELS, ORIGEM_GRUPOS, HGR_SUBGRUPOS, todayLocal } from '../lib/utils';

const NovaInternacao = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { pacienteId } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingPaciente, setLoadingPaciente] = useState(true);
  const [error, setError] = useState('');
  const [internacaoAtiva, setInternacaoAtiva] = useState(null);
  const [paciente, setPaciente] = useState(null);
  const [medicos, setMedicos] = useState([]);
  
  const today = todayLocal();
  
  const [formData, setFormData] = useState({
    data_internacao: today,
    diagnostico_principal: '',
    cid10: '',
    diagnosticos_secundarios: '',
    origem_grupo: '',
    origem_subgrupo: '',
    convenio_id: '',
    medico_id: '',
    medico_solicitante_id: '',
    especialidades: [],
    profissionais_especialidades: {},
    status_kanban: 'admissao_pendente',
    previsao_alta: '',
    risco: 'baixo'
  });

  const [medicosSolicitantes, setMedicosSolicitantes] = useState([]);
  const [profissionaisEsp, setProfissionaisEsp] = useState({});

  const ESP_PERFIL_MAP = {
    fisio: 'fisioterapeuta',
    fono: 'fonoaudiologo',
    nutri: 'nutricionista',
    psicologo: 'psicologo',
    clinico: 'medico',
    enfermeiro: 'enfermeiro',
  };

  useEffect(() => {
    fetchPaciente();
    fetchMedicos();
  }, [pacienteId]);

  // Fetch medicos filtered by unidade_origem when origem changes
  useEffect(() => {
    const fetchSolicitantes = async () => {
      if (!formData.origem_grupo) {
        setMedicosSolicitantes([]);
        return;
      }
      try {
        const res = await api.get(`/medicos?ativo=true&unidade_origem=${formData.origem_grupo}`);
        setMedicosSolicitantes(res.data || []);
      } catch { setMedicosSolicitantes([]); }
    };
    fetchSolicitantes();
  }, [formData.origem_grupo, api]);

  // Fetch specialty professionals when specialties change
  useEffect(() => {
    const fetchProfs = async () => {
      const newMap = {};
      for (const esp of formData.especialidades) {
        const perfil = ESP_PERFIL_MAP[esp];
        if (!perfil) continue;
        try {
          const res = await api.get(`/usuarios?perfil=${perfil}`);
          newMap[esp] = (res.data || []).filter(u => u.ativo !== false);
        } catch { newMap[esp] = []; }
      }
      setProfissionaisEsp(newMap);
    };
    if (formData.especialidades.length > 0) fetchProfs();
    else setProfissionaisEsp({});
  }, [formData.especialidades.join(','), api]);

  const fetchPaciente = async () => {
    try {
      const response = await api.get(`/pacientes/${pacienteId}`);
      setPaciente(response.data);
      if (response.data?.convenio_padrao_id) setFormData((f) => ({ ...f, convenio_id: f.convenio_id || response.data.convenio_padrao_id }));
    } catch (error) {
      toast.error('Paciente não encontrado');
      navigate('/pacientes');
    } finally {
      setLoadingPaciente(false);
    }
  };

  const fetchMedicos = async () => {
    try {
      const response = await api.get('/medicos?ativo=true');
      setMedicos(response.data);
    } catch (error) {
      console.error('Error fetching medicos:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.diagnostico_principal.trim()) {
      setError('Diagnóstico principal é obrigatório');
      return;
    }

    if (!formData.data_internacao) {
      setError('Data de internação é obrigatória');
      return;
    }

    if (formData.origem_grupo === 'HGR' && !formData.origem_subgrupo) {
      setError('Selecione o subgrupo para HGR');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/internacoes', {
        ...formData,
        convenio_id: formData.convenio_id || null,
        paciente_id: pacienteId,
        origem: formData.origem_grupo ? formData.origem_grupo.toLowerCase() : '',
      });
      
      toast.success('Internação registrada com sucesso!');
      navigate(`/pacientes/${pacienteId}/internacao/${response.data.id}`);
    } catch (err) {
      const det = err.response?.data?.detail;
      if (err.response?.status === 409 && det?.internacao_ativa) {
        setError(`${det.message} Prontuário ativo: ${det.internacao_ativa.numero_prontuario || '—'} (admissão ${det.internacao_ativa.data_internacao?.slice(0, 10).split('-').reverse().join('/')}).`);
        setInternacaoAtiva(det.internacao_ativa);
      } else {
        setError(typeof det === 'string' ? det : det?.message || 'Erro ao registrar internação');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingPaciente) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="nova-internacao-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            data-testid="btn-voltar"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Nova Internação</h1>
            <p className="text-muted-foreground mt-1">
              Registrar novo episódio de internação
            </p>
          </div>
        </div>

        {/* Patient Info */}
        {paciente && (
          <Card className="bg-muted/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-accent/10">
                  <User className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="font-semibold">{paciente.nome}</p>
                  <p className="text-sm text-muted-foreground">
                    CPF: {paciente.cpf_mascarado || paciente.cpf}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Dados da Internação</CardTitle>
            <CardDescription>
              Preencha os dados do episódio de internação domiciliar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" data-testid="form-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="data_internacao">Data de Internação *</Label>
                  <Input
                    id="data_internacao"
                    type="date"
                    value={formData.data_internacao}
                    onChange={(e) => handleChange('data_internacao', e.target.value)}
                    required
                    data-testid="input-data-internacao"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="origem_grupo">Grupo de Origem *</Label>
                  <Select 
                    value={formData.origem_grupo} 
                    onValueChange={(value) => {
                      setFormData(prev => ({ ...prev, origem_grupo: value, origem_subgrupo: value === 'HGR' ? prev.origem_subgrupo : '', medico_solicitante_id: '' }));
                    }}
                  >
                    <SelectTrigger data-testid="select-origem-grupo">
                      <SelectValue placeholder="Selecione o grupo" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ORIGEM_GRUPOS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.origem_grupo === 'HGR' && (
                  <div className="space-y-2">
                    <Label htmlFor="origem_subgrupo">Subgrupo (HGR) *</Label>
                    <Select 
                      value={formData.origem_subgrupo} 
                      onValueChange={(value) => handleChange('origem_subgrupo', value)}
                    >
                      <SelectTrigger data-testid="select-origem-subgrupo">
                        <SelectValue placeholder="Selecione o subgrupo" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(HGR_SUBGRUPOS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <ConvenioSelect value={formData.convenio_id} onChange={(v) => setFormData({ ...formData, convenio_id: v })} />

                {formData.origem_grupo && (
                  <div className="space-y-2">
                    <Label htmlFor="medico_solicitante_id">Médico solicitante ({ORIGEM_GRUPOS[formData.origem_grupo] || formData.origem_grupo})</Label>
                    <Select 
                      value={formData.medico_solicitante_id} 
                      onValueChange={(value) => handleChange('medico_solicitante_id', value)}
                    >
                      <SelectTrigger data-testid="select-medico-solicitante">
                        <SelectValue placeholder="Selecione o médico" />
                      </SelectTrigger>
                      <SelectContent>
                        {medicosSolicitantes.length > 0 ? (
                          medicosSolicitantes.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.nome}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="_none" disabled>Nenhum médico cadastrado para {ORIGEM_GRUPOS[formData.origem_grupo]}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Especialidades */}
                <div className="space-y-2 md:col-span-2">
                  <Label>Especialidades necessárias</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'fisio', label: 'Fisioterapia', color: 'border-green-400 bg-green-50 text-green-700' },
                      { key: 'fono', label: 'Fonoaudiologia', color: 'border-purple-400 bg-purple-50 text-purple-700' },
                      { key: 'nutri', label: 'Nutrição', color: 'border-orange-400 bg-orange-50 text-orange-700' },
                      { key: 'psicologo', label: 'Psicologia', color: 'border-blue-400 bg-blue-50 text-blue-700' },
                      { key: 'clinico', label: 'Clínico', color: 'border-rose-400 bg-rose-50 text-rose-700' },
                      { key: 'enfermeiro', label: 'Enfermeiro(a)', color: 'border-teal-400 bg-teal-50 text-teal-700' },
                    ].map(esp => {
                      const isSelected = (formData.especialidades || []).includes(esp.key);
                      return (
                        <button
                          key={esp.key}
                          type="button"
                          onClick={() => {
                            const newEsp = isSelected
                              ? formData.especialidades.filter(e => e !== esp.key)
                              : [...(formData.especialidades || []), esp.key];
                            const newProf = { ...(formData.profissionais_especialidades || {}) };
                            if (isSelected) delete newProf[esp.key];
                            setFormData(prev => ({ ...prev, especialidades: newEsp, profissionais_especialidades: newProf }));
                          }}
                          className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${
                            isSelected ? esp.color : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                          data-testid={`tag-esp-${esp.key}`}
                        >
                          {isSelected && '✓ '}{esp.label}
                        </button>
                      );
                    })}
                  </div>
                  {/* Professional selectors for active specialties */}
                  {(formData.especialidades || []).length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg mt-2">
                      {[
                        { key: 'fisio', label: 'Fisioterapeuta', color: 'text-green-700' },
                        { key: 'fono', label: 'Fonoaudiólogo', color: 'text-purple-700' },
                        { key: 'nutri', label: 'Nutricionista', color: 'text-orange-700' },
                        { key: 'psicologo', label: 'Psicólogo', color: 'text-blue-700' },
                        { key: 'clinico', label: 'Clínico', color: 'text-rose-700' },
                        { key: 'enfermeiro', label: 'Enfermeiro(a)', color: 'text-teal-700' },
                      ].filter(esp => (formData.especialidades || []).includes(esp.key)).map(esp => (
                        <div key={esp.key} className="space-y-1">
                          <Label className={`text-xs font-medium ${esp.color}`}>Profissional {esp.label}</Label>
                          <Select
                            value={(formData.profissionais_especialidades || {})[esp.key] || ''}
                            onValueChange={(v) => setFormData(prev => ({
                              ...prev,
                              profissionais_especialidades: {
                                ...(prev.profissionais_especialidades || {}),
                                [esp.key]: v === '_none' ? '' : v
                              }
                            }))}
                          >
                            <SelectTrigger className="h-9" data-testid={`select-prof-${esp.key}`}>
                              <SelectValue placeholder="Selecione o profissional" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">Nenhum</SelectItem>
                              {(profissionaisEsp[esp.key] || []).map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                              ))}
                              {(profissionaisEsp[esp.key] || []).length === 0 && (
                                <SelectItem value="_empty" disabled>Nenhum {esp.label.toLowerCase()} cadastrado</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="diagnostico_principal">Diagnóstico Principal *</Label>
                  <Textarea
                    id="diagnostico_principal"
                    placeholder="Descrição do diagnóstico principal"
                    value={formData.diagnostico_principal}
                    onChange={(e) => handleChange('diagnostico_principal', e.target.value)}
                    required
                    rows={2}
                    data-testid="input-diagnostico"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cid10">CID-10</Label>
                  <CidSearch
                    value={formData.cid10}
                    onChange={(code) => handleChange('cid10', code)}
                    placeholder="Pesquisar CID-10 (código ou doença)..."
                    api={api}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="risco">Risco/Complexidade</Label>
                  <Select 
                    value={formData.risco} 
                    onValueChange={(value) => handleChange('risco', value)}
                  >
                    <SelectTrigger data-testid="select-risco">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baixo">Baixo</SelectItem>
                      <SelectItem value="medio">Médio</SelectItem>
                      <SelectItem value="alto">Alto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="diagnosticos_secundarios">Diagnósticos Secundários</Label>
                  <Textarea
                    id="diagnosticos_secundarios"
                    placeholder="Outros diagnósticos relevantes..."
                    value={formData.diagnosticos_secundarios}
                    onChange={(e) => handleChange('diagnosticos_secundarios', e.target.value)}
                    rows={2}
                    data-testid="input-diagnosticos-secundarios"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="medico_id">Médico(a) responsável</Label>
                  <Select 
                    value={formData.medico_id} 
                    onValueChange={(value) => handleChange('medico_id', value)}
                  >
                    <SelectTrigger data-testid="select-medico">
                      <SelectValue placeholder="Selecione o médico(a)" />
                    </SelectTrigger>
                    <SelectContent>
                      {medicos.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="previsao_alta">Previsão de Alta</Label>
                  <Input
                    id="previsao_alta"
                    type="date"
                    value={formData.previsao_alta}
                    onChange={(e) => handleChange('previsao_alta', e.target.value)}
                    data-testid="input-previsao-alta"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status_kanban">Status Inicial</Label>
                  <Select 
                    value={formData.status_kanban} 
                    onValueChange={(value) => handleChange('status_kanban', value)}
                  >
                    <SelectTrigger data-testid="select-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).filter(([k]) => k !== 'alta_efetivada').map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => navigate(-1)}
                  data-testid="btn-cancelar"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  data-testid="btn-salvar"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Registrar Internação
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NovaInternacao;
